// File: src/pages/NavigateSpotPage.jsx

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TbNavigationPin } from 'react-icons/tb';
import { LuSend, LuChevronRight } from 'react-icons/lu';
import { useMatchFlow } from './useMatchFlow';
import EventProgressNavBar from './EventProgressNavBar';
import EndMatchBottomSheet from './EndMatchBottomSheet';
import { enforceSpeedmatchState, bindRoutePulseEnforcer } from '../../utils/speedmatchEnforcer';

/* ───────────────────────── Helpers ───────────────────────── */
function authHeaders() {
  const token =
    localStorage.getItem('accessToken') ||
    JSON.parse(localStorage.getItem('tribe_auth') || '{}')?.token ||
    '';
  return token ? { Authorization: `Bearer ${token}` } : {};
}
function avatarUrl(u) {
  return u?.profilePicUrl || '/default-avatar.png';
}
function formatName(u) {
  if (!u) return 'User';
  const fn = u.firstName || 'User';
  const ln = u.lastName ? ` ${u.lastName.charAt(0).toUpperCase()}.` : '';
  return `${fn}${ln}`;
}
function clockFromMs(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return { h: '00', m: '00', s: '00' };
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return {
    h: String(h).padStart(2, '0'),
    m: String(m).padStart(2, '0'),
    s: String(s).padStart(2, '0'),
  };
}

/* ───────────────────────── Component ───────────────────────── */
export default function NavigateSpotPage() {
  const navigate = useNavigate();
  const location = useLocation();     // ✅ add this line

  const seedFromState = location.state?.matchId || null;
  const {
    socket,
    match: currentMatch,
    foundPartner,
    endSession,
    setMatch,
    setIsFinding,
  } = useMatchFlow();

  const userId = localStorage.getItem('userId') || null;

  const api = process.env.REACT_APP_API_URL;
  const eventId =
    (currentMatch?.event?._id) ||
    localStorage.getItem('mobileEventId') ||
    null;

  // Back-intent window (set by CodeVerification "Back")
  const backForceUntil = (() => {
    try {
      if (!eventId) return 0;
      return Number(sessionStorage.getItem(`speedmatch_force_navigate:${eventId}`) || 0);
    } catch { return 0; }
  })();
  const backIntentActive = backForceUntil && Date.now() < backForceUntil;

  const didRunOnceRef = useRef(false);
  const closingRef = useRef(false);

  // Track when enforcer can safely run
  const enforcerCheckedRef = useRef(false);

  // NEW: rehydrate coordination
  const LAST_MATCH_KEY = 'sm:lastFoundMatchId';
  const [rehydrateTried, setRehydrateTried] = useState(false);

  // Timer
  const [tparts, setTparts] = useState({ h: '--', m: '--', s: '--' });
  const hasHours = tparts.h !== '00' && tparts.h !== '--';

  // Chat state
  const [roomId, setRoomId] = useState(null);
  const [msgs, setMsgs] = useState([]); // [{ messageId, senderId, text, createdAt }]
  const [text, setText] = useState('');
  const listRef = useRef(null);
// ⬇️ 1) Add these in component state (near other useState/useRef)
const [sending, setSending] = useState(false);
const lastSentAtRef = useRef(0); // burst throttle (prevents double-tap spam)

  // Autosize textarea baseline
  const inputRef = useRef(null);
  const inputBarRef = useRef(null);
  const [baseHeight, setBaseHeight] = useState(0);
  const [inputBarH, setInputBarH] = useState(56);

  // Sheet
  const [showEndSheet, setShowEndSheet] = useState(false);

  // Chat scroll anchoring
  const chatTopAnchorRef = useRef(null);
  const [chatTop, setChatTop] = useState(220);     // fallback
  const [chatBottom, setChatBottom] = useState(72); // will sync to inputBarH

  const participants = currentMatch?.participants ?? [];
  const step = currentMatch?.flowStep || 2;

  // If we arrived with {state:{optimistic:true}}, clear it after first paint
  useEffect(() => {
    if (location.state?.optimistic) {
      navigate('.', { replace: true, state: {} });
    }
  }, [location.state?.optimistic, navigate]);

  /* ───────── Rehydrate match FIRST on hard refresh (before enforcer) ───────── */
  useEffect(() => {
    // Already have a match → nothing to rehydrate
    if (currentMatch?._id) {
      setRehydrateTried(true);
      return;
    }
  
    // Prefer a matchId passed via navigation state, then fall back to the last saved one
    const seedId =
      (typeof seedFromState !== 'undefined' && seedFromState) ||
      sessionStorage.getItem(LAST_MATCH_KEY);
  
    // Nothing to rehydrate with
    if (!seedId) {
      setRehydrateTried(true);
      return;
    }
  
    // Do NOT resurrect a match we just declined on this tab
    try {
      if (sessionStorage.getItem(`sm:declineExit:${seedId}`) === '1') {
        setRehydrateTried(true);
        return;
      }
    } catch {}
  
    (async () => {
      try {
        const r = await fetch(`${api}/matches/${seedId}`, { headers: authHeaders() });
        const data = await r.json().catch(() => ({}));
        if (data?.match) {
          setMatch(data.match);
          try { sessionStorage.setItem(LAST_MATCH_KEY, String(data.match._id)); } catch {}
        }
      } catch {
        // noop — we'll fall back to redirect logic elsewhere
      } finally {
        setRehydrateTried(true);
      }
    })();
    // NOTE: `seedFromState` should come from `const seedFromState = location.state?.matchId || null`
  }, [api, currentMatch?._id, setMatch, seedFromState]);
  
  // Keep LAST_MATCH_KEY updated when match exists
  useEffect(() => {
    if (!currentMatch?._id) return;
    try { sessionStorage.setItem(LAST_MATCH_KEY, String(currentMatch._id)); } catch {}
  }, [currentMatch?._id]);

  /* ───────── Do NOT replay FoundMatch overlay after arriving here ───────── */
  useEffect(() => {
    if (!currentMatch?._id) return;
    try { sessionStorage.setItem(`sm_found_seen:${currentMatch._id}`, '1'); } catch {}
  }, [currentMatch?._id]);

  /* ───────── Guards ───────── */
  // Run enforcer ONLY AFTER we tried rehydrating (prevents portal flash)
  useEffect(() => {
    let alive = true;
    if (!rehydrateTried) return;
    if (!eventId) {                 // nothing to enforce; allow redirect guard below to decide
      enforcerCheckedRef.current = true;
      return;
    }
  
    // If we're coming back from verify → navigate, let UI render immediately and
    // skip the first enforce during the back window to avoid flicker.
    if (backIntentActive) {
      enforcerCheckedRef.current = true;
      const delay = Math.max(0, backForceUntil - Date.now() + 20);
      const t = setTimeout(() => {
        enforceSpeedmatchState({ api, eventId, pageKind: 'navigate', navigate });
      }, delay);
      return () => clearTimeout(t);
    }
  
    (async () => {
      try {
        await enforceSpeedmatchState({
          api,
          eventId,
          pageKind: 'navigate',   // keep your current pageKind
          navigate,
        });
      } finally {
        if (alive) enforcerCheckedRef.current = true;
      }
    })();
  
    // 🔑 NEW: bind to cron pulse so we re-run enforcement every time a route_state event fires
    const cleanup = bindRoutePulseEnforcer({
      api,
      eventId,
      pageKind: 'navigate',
      navigate,
    });
  
    return () => {
      alive = false;
      cleanup();
    };
  }, [api, eventId, navigate, rehydrateTried, backIntentActive, backForceUntil]);

  // Re-enforce on tab focus (okay to run anytime after first pass)
  useEffect(() => {
    if (!eventId) return;
    const onFocus = () => {
      enforceSpeedmatchState({ api, eventId, pageKind: 'navigate', navigate });
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [api, eventId, navigate]);

  // Re-enforce on push (matchUpdated → enforce without refresh)
  useEffect(() => {
    if (!eventId) return;
    const onPush = () =>
      enforceSpeedmatchState({ api, eventId, pageKind: 'navigate', navigate });
    window.addEventListener('sm:match-updated', onPush);
    return () => window.removeEventListener('sm:match-updated', onPush);
  }, [api, eventId, navigate]);

  useEffect(() => {
    if (currentMatch?.status === 'completed') {
      const eId = currentMatch.event?._id || localStorage.getItem('mobileEventId');
      navigate(eId ? `/matchflow?eventId=${eId}` : '/matchflow');
    }
  }, [currentMatch, navigate]);

  // Socket lifecycle for end/expire (toast-free)
  useEffect(() => {
    if (!socket) return;

    const onExpired = ({ matchId }) => {
      if (!currentMatch || currentMatch._id !== matchId) return;
      if (closingRef.current) return;
      closingRef.current = true;
      setMatch(null);
      setIsFinding(false);
      navigate('/matchflow?expired=1');
    };

    const onEnded = ({ matchId, endedBy }) => {
      if (!currentMatch || currentMatch._id !== matchId) return;
      if (closingRef.current) return;
      closingRef.current = true;

      const eId = currentMatch.event?._id || localStorage.getItem('mobileEventId');
      if (String(endedBy) === String(userId)) {
        navigate(eId ? `/matchflow?eventId=${eId}&ended=me` : '/matchflow?ended=me');
      } else {
        navigate(eId ? `/matchflow?eventId=${eId}&ended=partner` : '/matchflow?ended=partner');
      }
      setMatch(null);
      setIsFinding(false);
    };

    socket.on('match_expired', onExpired);
    socket.on('match_ended', onEnded);
    return () => {
      socket.off('match_expired', onExpired);
      socket.off('match_ended', onEnded);
    };
  }, [socket, currentMatch, userId, navigate, setMatch, setIsFinding]);

  // Timer (toast-free): drives the header countdown
  useEffect(() => {
    // If we don't have an expiry yet, clear the timer display
    if (!currentMatch?.expiresAt) {
      setTparts({ h: '--', m: '--', s: '--' });
      return;
    }

    const end = new Date(currentMatch.expiresAt).getTime();

    // Use requestAnimationFrame for smoother, drift-free ticking
    let rafId = null;
    let lastSecond = -1;

    const tick = () => {
      const diff = end - Date.now();
      const seconds = Math.max(0, Math.floor(diff / 1000));
      if (seconds !== lastSecond) {
        setTparts(clockFromMs(diff));
        lastSecond = seconds;
      }
      if (seconds > 0) {
        rafId = window.requestAnimationFrame(tick);
      } else {
        // reached zero
        setTparts(clockFromMs(0));
      }
    };

    tick();
    return () => rafId && window.cancelAnimationFrame(rafId);
  }, [currentMatch?.expiresAt]);

  /* ───────── Chat room wiring ───────── */
  // Ensure room exists once accepted
  useEffect(() => {
    if (!socket || !currentMatch?._id) return;
    if (currentMatch.status === 'accepted' || currentMatch.status === 'navigating') {
      socket.emit('nav:ensure-room', { matchId: currentMatch._id });
    }
  }, [socket, currentMatch?._id, currentMatch?.status]);

  // nav:room-ready → join + load history
  useEffect(() => {
    if (!socket) return;
    function onRoomReady({ matchId, roomId: rid }) {
      if (!currentMatch || String(currentMatch._id) !== String(matchId)) return;
      setRoomId(rid);
      try { socket.emit('join', { roomId: rid }); } catch {}
      if (!rid) return;
      fetch(`${api}/chat/rooms/${rid}/messages`, { headers: authHeaders() })
        .then((r) => r.json())
        .then((data) => {
          const normalized = (data || []).map((m) => ({
            ...m,
            text: m.text ?? m.content,
            senderId: String(m.senderId || ''),
          }));
          setMsgs(normalized);
          requestAnimationFrame(() =>
            listRef.current?.scrollTo(0, listRef.current.scrollHeight)
          );
        })
        .catch(() => {});
    }
    socket.on('nav:room-ready', onRoomReady);
    return () => socket.off('nav:room-ready', onRoomReady);
  }, [socket, api, currentMatch]);

  // newMessage live stream
  useEffect(() => {
    if (!socket) return;
    function onNewMessage(m) {
      if (!m?.roomId || String(m.roomId) !== String(roomId)) return;
      setMsgs((prev) => [...prev, { ...m, text: m.text ?? m.content }]);
      requestAnimationFrame(() =>
        listRef.current?.scrollTo(0, listRef.current.scrollHeight)
      );
    }
    socket.on('newMessage', onNewMessage);
    return () => socket.off('newMessage', onNewMessage);
  }, [socket, roomId]);

  useLayoutEffect(() => {
    const measure = () => {
      if (!chatTopAnchorRef.current) return;
      const rect = chatTopAnchorRef.current.getBoundingClientRect();
      setChatTop(rect.bottom);
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, []);

// ⬇️ 2) Replace your current sendMessage with this guarded version
async function sendMessage() {
  const value = String(text || '').trim();
  if (!value || !roomId || sending) return;

  // simple burst throttle: block clicks/Enter within 450ms
  const now = Date.now();
  if (now - lastSentAtRef.current < 450) return;
  lastSentAtRef.current = now;

  setSending(true);

  // optimistic clear to avoid duplicate re-sends from Enter spam
  setText('');
  try {
    await fetch(`${api}/chat/rooms/${roomId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ text: value }),
    });
    requestAnimationFrame(() =>
      listRef.current?.scrollTo(0, listRef.current.scrollHeight)
    );
  } catch {
    // restore so user can retry if it failed
    setText(value);
  } finally {
    setSending(false);
  }
}

  /* ───────── Autosize & keyboard ───────── */
  function measureBase(el) {
    const cs = window.getComputedStyle(el);
    const lh = parseFloat(cs.lineHeight);
    const line = Number.isFinite(lh) ? lh : parseFloat(cs.fontSize) || 14;
    const pad =
      (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    const border =
      (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    return Math.ceil(line + pad + border);
  }
  function autosize(el, base) {
    if (!el || !base) return;
    const MAX = 120;
    el.style.height = 'auto';
    const need = el.scrollHeight;
    const next = Math.min(Math.max(need, base), MAX);
    el.style.height = next + 'px';
    el.style.overflowY = next >= MAX ? 'auto' : 'hidden';
  }

  useEffect(() => {
    requestAnimationFrame(() => {
      if (inputRef.current) {
        const b = measureBase(inputRef.current);
        setBaseHeight(b);
        inputRef.current.style.minHeight = b + 'px';  // allow growth from 1 line
        autosize(inputRef.current, b);
      }
    });
  }, []);

  useEffect(() => {
    autosize(inputRef.current, baseHeight);
  }, [text, baseHeight]);

  useEffect(() => {
    if (!inputBarRef.current) return;
    const node = inputBarRef.current;
    const measure = () => setInputBarH(node.getBoundingClientRect().height);
    measure();
    let ro = null;
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(measure);
      ro.observe(node);
    }
    return () => ro && ro.disconnect();
  }, []);

  function getFound(doc, uid) {
    if (!doc) return false;
    const fm = doc.foundMap;
    if (!fm) return false;
    if (typeof fm.get === 'function') return fm.get(String(uid)) === true;
    return fm[String(uid)] === true;
  }
  
  useEffect(() => {
    if (!currentMatch || !userId) return;
  
    // If we’re in the back-intent window, DO NOT auto-redirect to verify.
    const eid = currentMatch?.event?._id || localStorage.getItem('mobileEventId');
    let backBlock = 0;
    try {
      backBlock = Number(sessionStorage.getItem(`speedmatch_back_block:${eid}`) || 0);
    } catch {}
  
    if (backBlock && Date.now() < backBlock) {
      // stay optimistically on Navigate
      return;
    }
  
    if (getFound(currentMatch, userId)) {
      navigate('/code-verification', {
        replace: true,
        state: {
          matchId: currentMatch._id,
          stageEndsAt: currentMatch?.expiresAt
            ? new Date(currentMatch.expiresAt).getTime()
            : null,
          fromEnforcer: true
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // keep deps lean; we read eid and backBlock inside
    currentMatch?._id,
    currentMatch?.foundMap,
    currentMatch?.expiresAt,
    userId,
    navigate
  ]);
  
  useEffect(() => {
    setChatBottom(inputBarH + 8); // little air above the bar
  }, [inputBarH]);

  /* ───────── CTA actions ───────── */
  function handleFoundPartner() {
    if (!currentMatch?._id || !userId) return;
  
    // 1) Tell server: I (only me) have found my partner → server will set foundMap[uid]=true
    try {
      socket?.emit('foundPartner', { matchId: currentMatch._id, userId });
    } catch {}
  
    // 2) Let the enforcer reconcile (server will flip route → 'verify' for me)
    const eid =
      currentMatch?.event?._id || localStorage.getItem('mobileEventId');
    if (eid) {
      enforceSpeedmatchState({ api, eventId: eid, pageKind: 'navigate', navigate });
    }
  }
  
  function handleEndMatch() {
    if (!currentMatch?._id || !userId) return;
  
    // Prefer server/enforcer routing over client navigation
    endSession(currentMatch._id, userId, localStorage.getItem('sessionId') || null);
  
    const eid =
      currentMatch?.event?._id || localStorage.getItem('mobileEventId');
    if (eid) {
      enforceSpeedmatchState({ api, eventId: eid, pageKind: 'navigate', navigate });
    }
  }
  

  return (
    <div style={S.page}>
      <EventProgressNavBar
        title="MatchFlow"
        leftLabel="End"
        onLeftClick={() => setShowEndSheet(true)}
        currentStep={step}
        totalSteps={5}
      />

      <EndMatchBottomSheet
        visible={showEndSheet}
        onCancel={() => setShowEndSheet(false)}
        onConfirm={() => {
          setShowEndSheet(false);
          handleEndMatch();
        }}
      />

      {/* Header */}
      <div style={S.header.container}>
        <h1 style={S.header.title}>
          Find a&nbsp;
          <span style={S.header.highlight}>
            <span style={S.header.highlightText}>Partner</span>
          </span>
        </h1>
        <div style={S.header.helperCopy}>
          Say hello and coordinate where to meet 👋
        </div>

        {/* Timer (no subheading above it) */}
        <div style={S.timer.wrapper}>
          <div style={S.timer.label}>Time Ending in</div>
          <div style={S.timer.digits} aria-live="polite">
            {hasHours && (
              <>
                <span style={S.timer.digit}>{tparts.h[0]}</span>
                <span style={S.timer.digit}>{tparts.h[1]}</span>
                <span style={S.timer.colon}>:</span>
              </>
            )}
            <span style={S.timer.digit}>{tparts.m[0]}</span>
            <span style={S.timer.digit}>{tparts.m[1]}</span>
            <span style={S.timer.colon}>:</span>
            <span style={S.timer.digit}>{tparts.s[0]}</span>
            <span style={S.timer.digit}>{tparts.s[1]}</span>
          </div>
        </div>
    
        {/* Green pill CTA (like meeting spot pill) */}
        <button onClick={handleFoundPartner} style={S.cta.found}>
          <TbNavigationPin size={18} />
          <span>Found partners</span>
          <LuChevronRight style={{ marginLeft: 0 }} size={18} />
        </button>

        {/* Anchor for measuring the top of the chat list */}
        <div ref={chatTopAnchorRef} />
      </div>

      {/* CHAT + COMPOSER */}
      <div style={S.content}>
        <div
          ref={listRef}
          style={{ ...S.chat.list, top: chatTop, bottom: chatBottom }}
          className="show-scrollbar"
        >
          {roomId ? (
            msgs.length ? (
              msgs.map((m) => {
                const me = String(m.senderId) === String(userId);
                const author =
                  participants.find((u) => String(u._id) === String(m.senderId)) || null;
                const name = author ? formatName(author) : String(m.senderId).slice(-6);

                return (
                  <MessageRow
                    key={m.messageId || `${m.senderId}-${m.createdAt}`}
                    isMe={me}
                    avatar={author ? avatarUrl(author) : '/default-avatar.png'}
                    name={!me ? name : null}
                    text={m.text}
                  />
                );
              })
            ) : (
              <div style={S.chat.empty}>Say hi to kick things off.</div>
            )
          ) : (
            <div style={S.chat.pending}>
              Waiting for everyone to accept… your chat will appear here.
            </div>
          )}
        </div>
      </div>

      {/* Fixed bottom composer (same UX as ChatPage) */}
      <div ref={inputBarRef} style={S.composer.bar}>
        <div style={S.composer.inputWrapper}>
          <textarea
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={roomId ? 'Send Message…' : 'Chat unlocks when everyone accepts'}
            disabled={!roomId}
            rows={1}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!sending) sendMessage();
              }
            }}
            style={S.composer.input}
          />
<button
  type="button"
  aria-label={sending ? 'Sending…' : 'Send message'}
  style={{
    ...S.composer.sendIconBtn,
    cursor: roomId && text.trim() && !sending ? 'pointer' : 'default',
    opacity: roomId && text.trim() && !sending ? 1 : 0.5,
  }}
  disabled={!roomId || !text.trim() || sending}
  onClick={sendMessage}
>
  <LuSend size={18} color={roomId && text.trim() && !sending ? '#007BFF' : '#A0AEC0'} />
</button>

        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── UI atoms ───────────────────────── */
function MessageRow({ isMe, avatar, name, text }) {
  return (
    <div style={S.chat.row(isMe)}>
      {/* avatar on the outside edge */}
      {!isMe && (
        <img
          src={avatar}
          alt=""
          style={S.chat.bubbleAvatar}
          onError={(e) => { e.currentTarget.src = '/default-avatar.png'; }}
        />
      )}

      {/* bubble wrapper caps width; bubble fills it */}
      <div style={S.chat.bubbleWrap}>
        {!isMe && <div style={S.chat.author}>{name}</div>}
        <div
          style={{
            ...S.chat.bubble,
            ...(isMe ? S.chat.myMessageBubble : S.chat.theirMessageBubble),
          }}
        >
          {text}
        </div>
        {/* timestamp removed */}
      </div>

      {isMe && (
        <img
          src={avatar}
          alt=""
          style={S.chat.bubbleAvatar}
          onError={(e) => { e.currentTarget.src = '/default-avatar.png'; }}
        />
      )}
    </div>
  );
}

/* ───────────────────────── Styles ───────────────────────── */
const S = {
  page: {
    height: '100vh',
    background: '#fff',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },

  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },

  header: {
    container: {
      boxSizing: 'border-box',
      maxWidth: 680,
      width: '80%',
      margin: '12px auto 8px',
      padding: '0 12px',
      textAlign: 'center',
      color: '#0b0b0b',
       position: 'relative',
 zIndex: 30,   // keep header (and button) above the fade
    },
    title: {
      margin: 0,
      fontSize: '1.85rem',
      fontWeight: 800,
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    highlight: {
      display: 'inline-block',
      backgroundColor: '#3579F6',
      color: '#fff',
      padding: '3px 10px',
      paddingBottom: 4,
      borderRadius: 6,
      transform: 'skewX(-6deg) rotate(-2deg)',
      marginLeft: 0,
    },
    highlightText: { display: 'inline-block', transform: 'skewX(6deg) rotate(2deg)' },
    helperCopy: {
      marginTop: 12,
      fontSize: '.9rem',
      color: '#6b7280',
      fontWeight: 500,
      lineHeight: 1.45,
      marginBottom: 0,
    },
  },

  timer: {
    wrapper: { marginTop: 16, display: 'flex', flexDirection: 'column', alignItems: 'center' },
    label: {
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: 999,
      background: '#fff',
      border: '1px solid #e5e7eb',
      fontSize: '.7rem',
      color: '#374151',
      fontWeight: 800,
      letterSpacing: '.06em',
      textTransform: 'uppercase',
      marginBottom: 8,
    },
    digits: { display: 'flex', alignItems: 'baseline', gap: 4 },
    digit: { display: 'inline-block', fontSize: '2.6rem', lineHeight: 1, fontWeight: 900, color: '#0f172a' },
    colon: { fontSize: '2rem', fontWeight: 900, color: '#9ca3af', lineHeight: 1, padding: '8px 4px', transform: 'translateY(-5px)' },
  },

  chat: {
    list: {
      position: 'fixed',
      left: 0,
      right: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
      backgroundColor: '#FFFFFF',
      WebkitOverflowScrolling: 'touch',
      padding: '12px 14px',
      paddingTop: 20,
      zIndex: 10,
    },

    empty: {
      padding: 12,
      marginLeft: 45,
      marginRight: 45,
      textAlign: 'center',
      color: '#6b7280',
      fontWeight: 700,
      marginTop: -12,
    },
    pending: {
      padding: '12px 14px',
      textAlign: 'center',
      color: '#6B7280',          // subtle neutral
      fontWeight: 500,
      background: '#FFFFFF',     // no tint
      border: '1px solid #E7E9EE',
      borderRadius: 10,
      lineHeight: 1.4,
      boxShadow: '0 1px 0 rgba(0,0,0,0.02)', // very light depth
      fontSize: 14,              // or '0.8125rem' for 13px
  fontWeight: 600,           // lighter than 700
  lineHeight: 1.35,
  letterSpacing: '-0.01em',
  margin: '0px 10px 00px 10px'

    },
    

    row: (isMe) => ({
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: isMe ? 'flex-end' : 'flex-start',
      gap: 8,
      marginBottom: 14,
    }),
    
    bubbleAvatar: {
      width: 32,
      height: 32,
      borderRadius: '50%',
      objectFit: 'cover',
      flexShrink: 0,
    },
  
    // cap once here; bubble uses 100% so no double-capping
    bubbleWrap: {
      maxWidth: 'min(78%, 560px)', // matches ChatPage feel, prevents early wraps
      minWidth: 0,                 // allow shrink inside flex
    },
  
    author: {
      fontSize: 12,
      color: '#6b7280',
      marginBottom: 4,
      fontWeight: 800,
    },
  
    bubble: {
      display: 'inline-block',
      padding: '12px 14px',
      borderRadius: 14,
      maxWidth: '100%',           // let wrapper decide the cap
      boxSizing: 'border-box',
  
      // natural wrapping; only break long tokens/URLs if needed
      whiteSpace: 'pre-wrap',
      overflowWrap: 'break-word',
      wordBreak: 'break-word',
      hyphens: 'auto',
    },
  
    myMessageBubble: {
      background: '#007BFF',
      color: '#fff',
      boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
    },
  
    theirMessageBubble: {
      background: '#E0E0E0',
      color: '#111',
      boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
    },
  
    ts: { fontSize: 12, color: '#6B7280', marginTop: 6 },
  },

  composer: {
    bar: {
      position: 'fixed',
      left: 0,
      right: 0,
      bottom: 0,
      padding: '10px 12px calc(10px + env(safe-area-inset-bottom, 0px))',
      background: '#fff',
      zIndex: 20,
    },
    inputWrapper: { position: 'relative', display: 'flex', gap: 8 },
    input: {
      flex: 1,
      borderRadius: 10,
      border: '1px solid #E7E9EE',
      padding: '10px 40px 10px 12px',
      fontSize: 14,
      outline: 'none',
      background: '#fff',
      resize: 'none',
      lineHeight: 1.4,
      boxSizing: 'border-box',
      maxHeight: 120,          // grows until cap
      overflowY: 'hidden',     // JS flips to 'auto' at cap
      fontFamily: "'Open Sans', sans-serif",   // ← explicit Open Sans

    },

    sendIconBtn: {
      position: 'absolute',
      top: '50%',
      right: 8,
      transform: 'translateY(-50%)',
      width: 28,
      height: 28,
      borderRadius: 999,
      border: 'none',
      background: 'transparent',
      display: 'grid',
      placeItems: 'center',
      cursor: 'pointer',
      padding: 0,
    },
  },

  cta: {
    found: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      padding: '8px 8px',
      borderRadius: 999,
      background: '#fff',
      color: '#0b0b0b',
      fontWeight: 900,
      letterSpacing: 0.2,
      border: '1px solid #0b0b0b',
      marginTop: 8,
      cursor: 'pointer',
      transition: 'background .15s ease, transform .06s ease',
      boxShadow: '0 0 0 0 rgba(0,0,0,0)',
    },
  
    
  },
};






